import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMenu, FiX, FiShield, FiPhone, FiChevronDown } = FiIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', href: '/' },
    { 
      name: 'Services', 
      href: '/services', 
      hasDropdown: true,
      dropdownItems: [
        { name: 'All Services', href: '/services' },
        { name: 'Data Governance', href: '/data-governance' },
        { name: 'Compliance Management', href: '/compliance-management' },
        { name: 'Data Loss Prevention', href: '/data-loss-prevention' },
        { name: 'Information Protection', href: '/information-protection' }
      ]
    },
    { name: 'About', href: '/about' },
    { name: 'Case Studies', href: '/case-studies' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact', href: '/contact' },
  ];

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <SafeIcon icon={FiShield} className="h-8 w-8 text-blue-600" />
            <span className="text-2xl font-bold text-gray-900">YourPurviewPartner</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <div key={item.name} className="relative">
                {item.hasDropdown ? (
                  <div 
                    className="flex items-center space-x-1 cursor-pointer"
                    onMouseEnter={() => setIsServicesDropdownOpen(true)}
                    onMouseLeave={() => setIsServicesDropdownOpen(false)}
                  >
                    <Link 
                      to={item.href} 
                      className={`font-medium transition-colors flex items-center space-x-1 ${
                        location.pathname === item.href || item.dropdownItems?.some(d => location.pathname === d.href)
                          ? 'text-blue-600 border-b-2 border-blue-600 pb-1' 
                          : 'text-gray-700 hover:text-blue-600'
                      }`}
                    >
                      <span>{item.name}</span>
                      <SafeIcon icon={FiChevronDown} className="h-4 w-4" />
                    </Link>

                    {/* Dropdown Menu */}
                    {isServicesDropdownOpen && (
                      <motion.div 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200"
                      >
                        {item.dropdownItems.map((dropdownItem) => (
                          <Link
                            key={dropdownItem.href}
                            to={dropdownItem.href}
                            className={`block px-4 py-3 text-sm transition-colors ${
                              location.pathname === dropdownItem.href
                                ? 'bg-blue-50 text-blue-600 font-medium'
                                : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                            }`}
                          >
                            {dropdownItem.name}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </div>
                ) : (
                  <Link 
                    to={item.href} 
                    className={`font-medium transition-colors ${
                      location.pathname === item.href
                        ? 'text-blue-600 border-b-2 border-blue-600 pb-1' 
                        : 'text-gray-700 hover:text-blue-600'
                    }`}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <Link 
              to="/purview-audit" 
              className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
            >
              Purview Audit
            </Link>
            <a href="tel:404-480-3547" className="flex items-center space-x-2 text-gray-700 hover:text-blue-600">
              <SafeIcon icon={FiPhone} className="h-4 w-4" />
              <span className="font-medium">Call Now</span>
            </a>
          </div>

          {/* Mobile menu button */}
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)} 
            className="md:hidden p-2"
          >
            <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="h-6 w-6" />
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden py-4 border-t border-gray-200"
          >
            <nav className="flex flex-col space-y-4">
              {navigation.map((item) => (
                <div key={item.name}>
                  {item.hasDropdown ? (
                    <div className="space-y-2">
                      <Link 
                        to={item.href} 
                        onClick={() => setIsMenuOpen(false)}
                        className={`font-medium ${
                          location.pathname === item.href || item.dropdownItems?.some(d => location.pathname === d.href)
                            ? 'text-blue-600' 
                            : 'text-gray-700'
                        }`}
                      >
                        {item.name}
                      </Link>
                      <div className="pl-4 space-y-2">
                        {item.dropdownItems.map((dropdownItem) => (
                          <Link
                            key={dropdownItem.href}
                            to={dropdownItem.href}
                            onClick={() => setIsMenuOpen(false)}
                            className={`block text-sm ${
                              location.pathname === dropdownItem.href
                                ? 'text-blue-600 font-medium'
                                : 'text-gray-600 hover:text-blue-600'
                            }`}
                          >
                            {dropdownItem.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Link 
                      to={item.href} 
                      onClick={() => setIsMenuOpen(false)}
                      className="text-gray-700 hover:text-blue-600 font-medium"
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
              <Link 
                to="/purview-audit" 
                onClick={() => setIsMenuOpen(false)}
                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg font-semibold text-center"
              >
                Purview Audit
              </Link>
            </nav>
          </motion.div>
        )}
      </div>
    </header>
  );
};

export default Header;